import { useState, useEffect } from "react";
import {
  X,
  Upload,
  Video,
  Image as ImageIcon,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Loader,
} from "lucide-react";
import QuickAddCategoryModal from "./QuickAddCategoryModal";
import { useProductForm } from "../../hooks/useProductForm";
import { useNotification } from "../../context/NotificationContext";
import { validateProducts } from "../../utils/productValidator";

export default function AddEditProductModal({
  isOpen,
  isEditMode = false,
  product = null,
  categories,
  onClose,
  onSave,
  onAddCategory,
}) {
  const { showNotification } = useNotification();
  const {
    productsForm,
    updateProductField,
    updateSpecificationField,
    handleImageUpload,
    handleRemoveImage,
    handleVideoUpload,
    handleRemoveVideo,
    addAnotherProduct,
    removeProductForm,
    resetForm,
    getValidProducts,
    buildFormDataForCreate,
    buildFormDataForUpdate,
  } = useProductForm(categories);

  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      resetForm(isEditMode ? product : null);
      setExpandedIndex(0);
    }
  }, [isOpen, isEditMode, product, resetForm]);

  if (!isOpen) return null;



  const handleSave = async () => {
    // 🎨 Frontend Validation using the shared validator
    const validationErrors = validateProducts(productsForm);

    if (validationErrors.length > 0) {
      // Show descriptive notifications for each invalid row
      validationErrors.forEach(({ index, errors }) => {
        const errorMessages = Object.values(errors).join(", ");
        showNotification({
          type: "error",
          title: `Validation Error (Item ${index + 1})`,
          message: errorMessages,
          duration: 5000,
        });
      });
      return; // Stop submission
    }

    try {
      setIsSubmitting(true);
      const validProducts = productsForm; // Since we validated everything above

      const formData = isEditMode
        ? buildFormDataForUpdate(validProducts[0]) // edit is single
        : buildFormDataForCreate(validProducts);

      await onSave({
        mode: isEditMode ? "edit" : "create",
        productId: isEditMode ? product?.id : null,
        formData,
      });
    } catch (err) {
      console.error("Save failed in modal", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCategoryAdded = (newCategory) => {
    onAddCategory(newCategory);
    updateProductField(expandedIndex, "categoryId", newCategory.id.toString());
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 md:p-8 overflow-y-auto animate-in fade-in duration-300">
        <div className="bg-white dark:bg-[#1E1E1E] rounded-[2rem] w-full max-w-5xl my-auto flex flex-col max-h-[90vh] shadow-2xl overflow-hidden border border-[#E5E7EB] dark:border-[#2A2A2A]">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 md:p-8 border-b border-[#E5E7EB] dark:border-[#2A2A2A] flex-shrink-0 bg-white dark:bg-[#1E1E1E]">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#111111] dark:text-white font-poppins">
                {isEditMode ? "Edit Product" : "Add Products"}
              </h2>
              <p className="text-sm text-[#666666] dark:text-[#A0A0A0] font-inter mt-1">
                {isEditMode
                  ? "Update product details below"
                  : "Add one or multiple products at once"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-3 rounded-xl hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-all active:scale-90"
            >
              <X size={24} className="text-[#666666] dark:text-[#A0A0A0]" />
            </button>
          </div>

          {/* Modal Body - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6 custom-scrollbar">
            {productsForm.map((formData, idx) => (
              <div
                key={idx}
                className={`border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-[1.5rem] overflow-hidden transition-all duration-300 ${expandedIndex === idx ? "ring-2 ring-[#62397f]/20 bg-white dark:bg-[#1E1E1E]" : "bg-[#FAFAFA] dark:bg-[#1A1A1A]"}`}
              >
                {/* Product Form Header */}
                <div
                  className="flex items-center justify-between p-4 cursor-pointer"
                  onClick={() =>
                    setExpandedIndex(expandedIndex === idx ? -1 : idx)
                  }
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#62397f] text-white flex items-center justify-center font-bold text-sm">
                      {idx + 1}
                    </div>
                    <span className="font-semibold text-[#111111] dark:text-white font-poppins">
                      {formData.name || `Product ${idx + 1}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {productsForm.length > 1 && !isEditMode && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeProductForm(idx);
                        }}
                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                    {expandedIndex === idx ? (
                      <ChevronUp size={20} className="text-[#666666]" />
                    ) : (
                      <ChevronDown size={20} className="text-[#666666]" />
                    )}
                  </div>
                </div>

                {/* Collapsible Content */}
                {expandedIndex === idx && (
                  <div className="p-6 md:p-8 border-t border-[#E5E7EB] dark:border-[#2A2A2A] space-y-8 animate-in slide-in-from-top-4 duration-300">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div className="md:col-span-2 lg:col-span-2">
                        <label className="block text-sm font-semibold text-[#111111] dark:text-white font-inter mb-2">
                          Product Name *
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) =>
                            updateProductField(idx, "name", e.target.value)
                          }
                          placeholder="e.g., White Chiavari Chair"
                          className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#262626] text-[#111111] dark:text-white placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#62397f] transition-all text-sm font-inter"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#111111] dark:text-white font-inter mb-2">
                          Category *
                        </label>
                        <div className="relative">
                          <select
                            value={formData.categoryId}
                            onChange={(e) =>
                              updateProductField(
                                idx,
                                "categoryId",
                                e.target.value,
                              )
                            }
                            className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#262626] text-[#111111] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#62397f] transition-all text-sm font-inter appearance-none"
                          >
                            <option value="">Select category</option>
                            {categories.map((cat) => (
                              <option key={cat.id} value={cat.id.toString()}>
                                {cat.name}
                              </option>
                            ))}
                          </select>
                          <ChevronDown
                            size={16}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-[#666666] pointer-events-none"
                          />
                        </div>
                        {/* <button
                          onClick={() => setShowAddCategoryModal(true)}
                          className="mt-2 text-xs font-semibold text-[#62397f] hover:text-[#7C3AED] transition-colors"
                        >
                          + Create New Category
                        </button> */}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#111111] dark:text-white font-inter mb-2">
                          Price (per unit) *
                        </label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#666666] text-sm">
                            ₦
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            value={formData.price}
                            onChange={(e) =>
                              updateProductField(idx, "price", e.target.value)
                            }
                            placeholder="0.00"
                            className="w-full pl-8 pr-4 py-3 rounded-xl border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#262626] text-[#111111] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#62397f] transition-all text-sm font-inter"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#111111] dark:text-white font-inter mb-2">
                          Stock Quantity *
                        </label>
                        <input
                          type="number"
                          value={formData.quantity}
                          onChange={(e) =>
                            updateProductField(idx, "quantity", e.target.value)
                          }
                          placeholder="0"
                          className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#262626] text-[#111111] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#62397f] transition-all text-sm font-inter"
                        />
                      </div>

                      <div className="flex items-center pt-8">
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <div
                            className={`w-10 h-6 rounded-full p-1 transition-all ${formData.popular ? "bg-[#62397f]" : "bg-[#E5E7EB] dark:bg-[#2A2A2A]"}`}
                          >
                            <div
                              className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${formData.popular ? "translate-x-4" : "translate-x-0"}`}
                            />
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.popular}
                            onChange={(e) =>
                              updateProductField(
                                idx,
                                "popular",
                                e.target.checked,
                              )
                            }
                            className="hidden"
                          />
                          <span className="text-sm font-semibold text-[#111111] dark:text-white font-inter group-hover:text-[#62397f] transition-colors">
                            Popular Product
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-semibold text-[#111111] dark:text-white font-inter mb-2">
                        Product Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) =>
                          updateProductField(idx, "description", e.target.value)
                        }
                        placeholder="Write a detailed description for this product..."
                        rows={3}
                        className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#262626] text-[#111111] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#62397f] transition-all text-sm font-inter resize-none"
                      />
                    </div>

                    {/* Specs & Media Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Media Upload */}
                      <div className="space-y-4">
                        <h4 className="text-sm font-bold text-[#111111] dark:text-white font-poppins uppercase tracking-wider">
                          Product Media
                        </h4>

                        <div>
                          <p className="text-xs text-[#666666] dark:text-[#A0A0A0] mb-3">
                            Images (Minimum 1, Maximum 3)
                          </p>
                          <div className="grid grid-cols-3 gap-3">
                            {formData.imagePreviews.map((img, iIdx) => (
                              <div
                                key={iIdx}
                                className="relative aspect-square group"
                              >
                                <img
                                  src={img}
                                  alt=""
                                  className="w-full h-full object-cover rounded-xl border border-[#E5E7EB] dark:border-[#2A2A2A]"
                                />
                                <button
                                  onClick={() => handleRemoveImage(idx, iIdx)}
                                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-all scale-90 hover:scale-110"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            ))}
                            {formData.imagePreviews.length < 3 && (
                              <label className="flex flex-col items-center justify-center aspect-square border-2 border-dashed border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl cursor-pointer hover:bg-[#62397f]/5 hover:border-[#62397f] transition-all group">
                                <ImageIcon
                                  size={24}
                                  className="text-[#9CA3AF] group-hover:text-[#62397f] transition-colors"
                                />
                                <span className="text-[10px] font-bold text-[#9CA3AF] group-hover:text-[#62397f] mt-2">
                                  ADD IMAGE
                                </span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={(e) =>
                                    handleImageUpload(idx, e.target.files)
                                  }
                                  className="hidden"
                                />
                              </label>
                            )}
                          </div>
                        </div>

                        <div>
                          <p className="text-xs text-[#666666] dark:text-[#A0A0A0] mb-3">
                            Product Video (Optional)
                          </p>
                          {formData.videoPreview ? (
                            <div className="relative aspect-video group">
                              <video
                                src={formData.videoPreview}
                                className="w-full h-full object-cover rounded-xl"
                              />
                              <button
                                onClick={() => handleRemoveVideo(idx)}
                                className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-xl opacity-0 group-hover:opacity-100 transition-all"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ) : (
                            <label className="flex items-center gap-4 p-4 border-2 border-dashed border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl cursor-pointer hover:bg-[#62397f]/5 hover:border-[#62397f] transition-all group">
                              <div className="w-12 h-12 rounded-xl bg-white dark:bg-[#262626] border border-[#E5E7EB] dark:border-[#2A2A2A] flex items-center justify-center text-[#9CA3AF] group-hover:text-[#62397f] transition-colors">
                                <Video size={20} />
                              </div>
                              <div className="text-left">
                                <p className="text-sm font-bold text-[#111111] dark:text-white group-hover:text-[#62397f] transition-colors">
                                  Upload Video
                                </p>
                                <p className="text-[10px] text-[#666666] dark:text-[#A0A0A0]">
                                  MP4, MOV up to 50MB
                                </p>
                              </div>
                              <input
                                type="file"
                                accept="video/*"
                                onChange={(e) =>
                                  handleVideoUpload(idx, e.target.files[0])
                                }
                                className="hidden"
                              />
                            </label>
                          )}
                        </div>
                      </div>

                      {/* Specifications */}
                      <div className="space-y-4">
                        <h4 className="text-sm font-bold text-[#111111] dark:text-white font-poppins uppercase tracking-wider">
                          Specifications
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-[10px] font-bold text-[#666666] dark:text-[#A0A0A0] mb-1.5 uppercase tracking-wide">
                              Weight
                            </label>
                            <input
                              type="text"
                              value={formData.specifications.weight}
                              onChange={(e) =>
                                updateSpecificationField(
                                  idx,
                                  "weight",
                                  e.target.value,
                                )
                              }
                              placeholder="e.g. 8 lbs"
                              className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#262626] text-sm font-inter focus:ring-2 focus:ring-[#62397f] outline-none transition-all"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-[#666666] dark:text-[#A0A0A0] mb-1.5 uppercase tracking-wide">
                              Color
                            </label>
                            <input
                              type="text"
                              value={formData.specifications.colour}
                              onChange={(e) =>
                                updateSpecificationField(
                                  idx,
                                  "colour",
                                  e.target.value,
                                )
                              }
                              placeholder="e.g. White"
                              className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#262626] text-sm font-inter focus:ring-2 focus:ring-[#62397f] outline-none transition-all"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-[#666666] dark:text-[#A0A0A0] mb-1.5 uppercase tracking-wide">
                              Material
                            </label>
                            <input
                              type="text"
                              value={formData.specifications.material}
                              onChange={(e) =>
                                updateSpecificationField(
                                  idx,
                                  "material",
                                  e.target.value,
                                )
                              }
                              placeholder="e.g. Wood"
                              className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#262626] text-sm font-inter focus:ring-2 focus:ring-[#62397f] outline-none transition-all"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-[#666666] dark:text-[#A0A0A0] mb-1.5 uppercase tracking-wide">
                              Dimensions
                            </label>
                            <input
                              type="text"
                              value={formData.specifications.dimensions}
                              onChange={(e) =>
                                updateSpecificationField(
                                  idx,
                                  "dimensions",
                                  e.target.value,
                                )
                              }
                              placeholder='e.g. 36"H x 16"W'
                              className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#262626] text-sm font-inter focus:ring-2 focus:ring-[#62397f] outline-none transition-all"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {!isEditMode && (
              <button
                onClick={() => {
                  addAnotherProduct();
                  setExpandedIndex(productsForm.length);
                }}
                className="w-full py-4 border-2 border-dashed border-[#E5E7EB] dark:border-[#2A2A2A] rounded-[1.5rem] flex items-center justify-center gap-3 text-[#666666] hover:bg-[#62397f]/5 hover:border-[#62397f] hover:text-[#62397f] transition-all font-bold text-sm uppercase tracking-wider group"
              >
                <div className="w-8 h-8 rounded-full bg-[#F3F4F6] dark:bg-[#262626] group-hover:bg-[#62397f] group-hover:text-white flex items-center justify-center transition-colors">
                  <Plus size={20} />
                </div>
                Add Another Product Entry
              </button>
            )}
          </div>

          {/* Modal Footer */}
          <div className="p-6 md:p-8 border-t border-[#E5E7EB] dark:border-[#2A2A2A] flex flex-col sm:flex-row items-center justify-end gap-4 bg-[#FAFAFA] dark:bg-[#1A1A1A]">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-8 py-3.5 border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111111] dark:text-white rounded-2xl font-bold text-sm font-inter transition-all hover:bg-white dark:hover:bg-[#262626] active:scale-95"
            >
              Discard Changes
            </button>
            <button
              onClick={handleSave}
              disabled={getValidProducts().length === 0 || isSubmitting}
              className="w-full sm:w-auto px-10 py-3.5 bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white rounded-2xl font-bold text-sm font-inter transition-all shadow-lg shadow-[#62397f]/20 hover:shadow-xl hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting && <Loader size={18} className="animate-spin" />}
              {isSubmitting
                ? (isEditMode ? "Updating..." : "Creating...")
                : (isEditMode
                  ? "Update Product"
                  : `Create ${getValidProducts().length} ${getValidProducts().length === 1 ? "Product" : "Products"}`)}
            </button>
          </div>
        </div>
      </div>

      <QuickAddCategoryModal
        isOpen={showAddCategoryModal}
        onClose={() => setShowAddCategoryModal(false)}
        onSave={handleCategoryAdded}
      />

      <style jsx="true">{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #E5E7EB;
          border-radius: 10px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #2A2A2A;
        }
      `}</style>
    </>
  );
}
